import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from "@mui/material";
import {
  Event as EventIcon,
  Star as StarIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { useUnifiedTranslation } from "../../hooks/useUnifiedTranslation";
import { participantApi } from "../../services/api/participant.api";
import type { ParticipantHistory } from "../../services/api/participant.api";
import { eventsApi } from "../../services/api/events.api";
import { DataTable } from "../ui/DataTable";
import type { DataTableColumn } from "../ui/DataTable";
import { ParticipantRole, ParticipantStatus } from "../../types/events-unified";
import {
  PARTICIPANT_ROLE_LABEL_KEYS,
  PARTICIPANT_STATUS_CONFIG,
  PARTICIPANT_STATUS_LABEL_KEYS,
} from "../../constants/event-status.constants";
import { formatShortDate } from "../../utils/date-utils";
import { PAGE_SIZE } from "../../constants/pagination";
import { logger } from "../../utils/logger";
import {
  registrationFailureOf,
  registrationFallbackMessage,
} from "../../utils/registration-failure";
import { isRegistrationDeadlinePassed } from "../../utils/events/registration-deadline";
import { bookableCapacity } from "../../utils/events/bookable-capacity";
import { asksParticipationModeByName } from "../../constants/event-format.constants";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`participant-tabpanel-${index}`}
      aria-labelledby={`participant-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const ParticipantDashboard: React.FC = () => {
  const { t } = useUnifiedTranslation({ namespace: "dashboard" });
  /*
   * Запасная подпись передаётся i18next как `defaultValue`, а не выбирается по
   * типу результата: `t()` на отсутствующем ключе возвращает САМ КЛЮЧ, и это тоже
   * строка — проверка типа была истинной всегда, а запасная подпись мёртвым кодом
   * (человек видел `participant.kpis.contacts`). AD-472.
   */
  const ts = (key: string, fallback: string): string =>
    String(t(key, { defaultValue: fallback }));
  /*
   * Статус и роль участника подписаны из пространства `events` — там же, где
   * их читает таблица участников события. Второй словарь для тех же девяти
   * статусов и восьми ролей развёл бы подписи одного и того же: человек видел
   * бы «Присутствовал» на одном экране и что-то своё на другом.
   */
  const { t: tEvents } = useUnifiedTranslation({ namespace: "events" });
  const labelOf = (key: string | undefined, fallback: string): string => {
    if (!key) return fallback;
    return String(tEvents(key, { defaultValue: fallback }));
  };
  const participationStatusLabel = (status: string): string =>
    labelOf(PARTICIPANT_STATUS_LABEL_KEYS[status as ParticipantStatus], status);
  const participantRoleLabel = (role: string): string =>
    labelOf(PARTICIPANT_ROLE_LABEL_KEYS[role as ParticipantRole], role);
  const [activeTab, setActiveTab] = useState(0);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /*
   * AD-415. ОТКАЗ ЗАГРУЗКИ НЕ ВЫДАЁТСЯ ЗА ПУСТОТУ.
   *
   * Каждый запрос отдаёт `isError` — и каждая вкладка ниже обязана его
   * прочитать. Прежде веток было две: «грузится» и «есть строки», а всё
   * остальное схлопывалось в «Нет мероприятий» / «История участия пуста».
   * Пятисотка с сервера при этом оставляла `data` неопределённой, и человек
   * читал утверждение о СВОИХ данных — «история пуста» — вместо сообщения о
   * сбое. Ровно так выглядел на бою сломанный `GET /participant/history`
   * (AD-418): экран уверенно сообщал, что человек нигде не участвовал.
   */
  // Загрузка предстоящих событий
  const {
    data: upcomingEventsData,
    isLoading: isLoadingEvents,
    isError: isEventsError,
  } = useQuery({
    queryKey: ["participant", "upcoming-events"],
    queryFn: async () => {
      const response = await participantApi.getUpcomingEvents({
        limit: PAGE_SIZE.SMALL,
      });
      return response.data;
    },
    refetchInterval: 60000, // Обновлять каждую минуту
  });

  // Загрузка рекомендаций
  const {
    data: recommendationsData,
    isLoading: isLoadingRecommendations,
    isError: isRecommendationsError,
  } = useQuery({
    queryKey: ["participant", "recommendations"],
    queryFn: async () => {
      const response = await participantApi.getRecommendations({
        limit: PAGE_SIZE.MICRO,
      });
      return response.data;
    },
  });

  // Загрузка истории
  const {
    data: historyData,
    isLoading: isLoadingHistory,
    isError: isHistoryError,
  } = useQuery({
    queryKey: ["participant", "history"],
    queryFn: async () => {
      const response = await participantApi.getHistory({
        limit: PAGE_SIZE.SMALL,
      });
      return response.data;
    },
  });

  // Загрузка KPI-метрик участника (реальные агрегаты BE: GET /participant/metrics).
  // Прежний вызов getEventStats() бил в per-event эндпоинт без eventId → 403 и
  // форму без полей дашборда; перенацелено на агрегатный /metrics (Вариант B).
  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ["participant", "metrics"],
    queryFn: async () => {
      const response = await participantApi.getMetrics();
      return response.data;
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Регистрация идёт КАНОНИЧЕСКИМ путём POST /events/:id/register (решение владельца по
  // входу 7 от 01.09.2026, таблица утверждения WORKSHEET.md §7.5). Прежний вызов
  // participantApi.registerForEvent бил во вторую дверь POST /participant/events/:id/register,
  // недостижимую барьером validateParticipantAccess (O-51): кнопка отказывала всегда.
  const [registerError, setRegisterError] = useState<string | null>(null);
  /*
   * AD-431. ИСХОД ЗАПИСИ, А НЕ ОДИН ЕГО ВАРИАНТ. У канона записи исходов два:
   * место (`registered`) и очередь (`waitlisted` — полное событие,
   * `settleSeatOrWaitlist`). Прежде здесь лежала одна строка, и «Регистрация
   * успешна» доставалась обоим: поставленный в очередь уходил уверенным, что
   * место за ним. Полярность хранится рядом с текстом, потому что от неё
   * зависит и вид сообщения: зелёная плашка — то же утверждение, сказанное
   * цветом.
   */
  const [registerDone, setRegisterDone] = useState<{
    text: string;
    waitlisted: boolean;
  } | null>(null);
  /*
   * AD-531. События, запись на которые сервер объявил закрытой («срок прошёл»,
   * «запись закрыта» или решение организатора). Слово сервера важнее подсказки часов: такой отказ
   * приходит и тогда, когда часы зрителя отстают, а повтор даст его же —
   * поэтому кнопка этой строки уходит, а соседние остаются.
   */
  const [closedByServer, setClosedByServer] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const registrationClosed = (eventId: string, deadline: string): boolean =>
    closedByServer.has(eventId) ||
    isRegistrationDeadlinePassed(deadline, new Date());

  const handleRegister = async (eventId: string) => {
    setRegisterError(null);
    setRegisterDone(null);
    try {
      const response = await eventsApi.registerForEvent(eventId);
      // Статуса в ответе может не быть вовсе — старый сервер в окне выкатки
      // (TD-31). Угадывать нечего: остаётся прежняя фраза об успехе.
      const waitlisted = response.data?.status === ParticipantStatus.WAITLISTED;
      setRegisterDone({
        text: waitlisted
          ? ts(
              "participant.registerWaitlisted",
              "Мест нет — вы в листе ожидания. Освободится место, мы сообщим.",
            )
          : ts("participant.registerSuccess", "Регистрация успешна"),
        waitlisted,
      });
      // Списки дашборда читают /participant/*, канон их не инвалидирует — делаем это здесь,
      // иначе зарегистрированное событие не появится до перезагрузки страницы.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["participant", "upcoming-events"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["participant", "recommendations"],
        }),
        queryClient.invalidateQueries({ queryKey: ["participant", "metrics"] }),
      ]);
    } catch (error: unknown) {
      // AD-384: прежде здесь стоял `handleApiError`, печатавший `message` ответа
      // как есть, — а все доменные отказы канона записи названы машинным кодом,
      // и кнопка кабинета показывала человеку `EVENT_NOT_OPEN_FOR_REGISTRATION`.
      // Разбор кодов общий с карточкой события (`utils/registration-failure.ts`),
      // фразы — свои, из пространства `dashboard`.
      logger.error("Ошибка регистрации:", error);
      const failure = registrationFailureOf(error);
      if (
        failure === "deadlinePassed" ||
        failure === "closed" ||
        failure === "rejected"
      ) {
        setClosedByServer((prev) => new Set(prev).add(eventId));
      }
      const generic = ts(
        "participant.registerError",
        "Не удалось зарегистрироваться",
      );
      setRegisterError(
        failure
          ? ts(`participant.registerErrors.${failure}`, generic)
          : registrationFallbackMessage(error, generic),
      );
    }
  };

  /*
   * AD-415. СТОЛБЦЫ ИСТОРИИ ПРИВЯЗАНЫ К ОТВЕТУ СЕРВЕРА КОМПИЛЯТОРОМ.
   *
   * `DataTableColumn<T>.id` объявлен как `keyof T | string`, и это НЕ гейт:
   * `id: 'date'` на строке, где такого поля нет, — законное выражение. Именно
   * поэтому прежние столбцы (`title`, `date`, `status`, `role`) молчаливо
   * печатали пустые ячейки, а `render: (row: any) => …` — который на деле
   * получал первым аргументом ЗНАЧЕНИЕ ячейки, а не строку — ронял вкладку на
   * `undefined.date`. Локальное сужение `id` до `keyof
   * ParticipantHistoryEventResponseDto` делает любое вымышленное имя ошибкой
   * типов, а не пустой ячейкой.
   */
  const historyColumns: Array<
    DataTableColumn<ParticipantHistory> & {
      id: keyof ParticipantHistory;
    }
  > = [
    {
      id: "eventTitle",
      label: ts("participant.columns.event", "Мероприятие"),
      sortable: true,
    },
    {
      id: "startDate",
      label: ts("participant.columns.date", "Дата"),
      sortable: true,
      render: (_value, row) => formatShortDate(row.startDate),
    },
    {
      id: "participationStatus",
      label: ts("participant.columns.status", "Статус"),
      // Цвет и подпись — из общей карты статусов участника, той же, что красит
      // чип в таблице участников события. Прежний разбор знал три значения из
      // девяти, а сервер до AD-418 присылал сюда вовсе не статус участия.
      render: (_value, row) => (
        <Chip
          label={participationStatusLabel(row.participationStatus)}
          color={
            PARTICIPANT_STATUS_CONFIG[
              row.participationStatus as ParticipantStatus
            ]?.color ?? "default"
          }
          size="small"
        />
      ),
    },
    {
      id: "participantRole",
      label: ts("participant.columns.role", "Роль"),
      render: (_value, row) => participantRoleLabel(row.participantRole),
    },
    {
      id: "rating",
      label: ts("participant.columns.rating", "Оценка"),
      render: (_value, row) => (row.rating ? `${row.rating}/5` : "—"),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {ts("participant.title", "Дашборд участника")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ts("participant.subtitle", "Управление участием в мероприятиях")}
        </Typography>
      </Box>

      {/* Исход регистрации доходит до человека: сообщение сервера, а не общий текст */}
      {registerError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setRegisterError(null)}
        >
          {registerError}
        </Alert>
      )}
      {registerDone && (
        <Alert
          severity={registerDone.waitlisted ? "warning" : "success"}
          sx={{ mb: 3 }}
          onClose={() => setRegisterDone(null)}
        >
          {registerDone.text}
        </Alert>
      )}

      {/* KPI карточки */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {ts(
                      "participant.kpis.eventsAttended",
                      "Посещено мероприятий",
                    )}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? (
                      <CircularProgress size={24} />
                    ) : (
                      metricsData?.attended || 0
                    )}
                  </Typography>
                </Box>
                <EventIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {ts("participant.kpis.contacts", "Контакты")}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? (
                      <CircularProgress size={24} />
                    ) : (
                      metricsData?.contacts || 0
                    )}
                  </Typography>
                </Box>
                <PeopleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {ts("participant.kpis.averageRating", "Средний рейтинг")}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? (
                      <CircularProgress size={24} />
                    ) : (
                      metricsData?.averageRating?.toFixed(1) || "0.0"
                    )}
                  </Typography>
                </Box>
                <StarIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {ts(
                      "participant.kpis.upcomingEvents",
                      "Предстоящие события",
                    )}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? (
                      <CircularProgress size={24} />
                    ) : (
                      metricsData?.upcoming || 0
                    )}
                  </Typography>
                </Box>
                <TrendingUpIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab
              label={ts("participant.tabs.upcoming", "Предстоящие")}
              icon={<EventIcon />}
              iconPosition="start"
            />
            {/*
             * AD-419 / решение Сергея B4 (22.09.2026): вкладка называется
             * тем, что в ней лежит. Подбора за списком нет — сервер отдаёт
             * ближайшие по дате публичные события, на которые человек ещё не
             * записан, одни и те же для всех. Слово «Рекомендации» обещало
             * персонализацию, которой в v1 не будет.
             */}
            <Tab
              label={ts("participant.tabs.nearest", "Ближайшие события")}
              icon={<StarIcon />}
              iconPosition="start"
            />
            <Tab
              label={ts("participant.tabs.history", "История")}
              icon={<HistoryIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Вкладка: Предстоящие события */}
        <TabPanel value={activeTab} index={0}>
          {isLoadingEvents ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isEventsError ? (
            <Alert severity="error">
              {ts(
                "participant.loadFailed.upcoming",
                "Не удалось загрузить предстоящие мероприятия",
              )}
            </Alert>
          ) : upcomingEventsData?.events &&
            upcomingEventsData.events.length > 0 ? (
            <List>
              {upcomingEventsData.events.map((event) => (
                <ListItem
                  key={event.id}
                  secondaryAction={
                    event.isRegistered ? (
                      <Chip
                        label={ts("participant.registered", "Зарегистрирован")}
                        color="success"
                        size="small"
                      />
                    ) : registrationClosed(
                        event.id,
                        event.registrationDeadline,
                      ) ? (
                      /*
                       * AD-531. Записаться нельзя — и кнопки нет. Прежде
                       * кнопка стояла и на событии с прошедшим сроком, а
                       * нажатие давало отказ плашкой над списком при той же
                       * кнопке в строке. У гибрида так же: вопрос о способе
                       * на закрытой записи никуда не ведёт. Правило срока —
                       * зеркало серверного, решает сервер.
                       */
                      <Chip
                        label={ts(
                          "participant.registrationClosed",
                          "Запись закрыта",
                        )}
                        variant="outlined"
                        size="small"
                      />
                    ) : asksParticipationModeByName(event.format) ? (
                      /*
                       * AD-411. У ГИБРИДА КНОПКА ВЕДЁТ ТУДА, ГДЕ ЗАДАН ВОПРОС.
                       *
                       * Прежде она записывала прямо отсюда и не спрашивала
                       * ничего — запись получала «не заявлен», и организатор
                       * гибрида не мог отличить «человек не ответил» от «его
                       * не спросили». Спросить в строке списка нечестно: там
                       * нет ни описания события, ни цены, ни расписания, то
                       * есть нет того, из чего человек решает, ехать ли ему.
                       * Поэтому кнопка не записывает молча, а называет, что
                       * сделает, и ведёт на карточку события — единственную
                       * дверь, которая спрашивает и умеет отправить ответ.
                       */
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        {ts(
                          "participant.chooseModeAndRegister",
                          "Выбрать способ и записаться",
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleRegister(event.id)}
                      >
                        {ts("participant.register", "Зарегистрироваться")}
                      </Button>
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar>
                      <EventIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={event.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {formatShortDate(event.startDate)}
                        </Typography>
                        {" — "}
                        {event.location}
                        <br />
                        {event.organizer} • {event.currentParticipants}/
                        {/* №245: места, на которые пускает запись; поля нет в
                          ответе — заявленные (TD-31(б)); вместимость не
                          известна — прочерк, а не выдуманное число. */}
                        {bookableCapacity(event) ?? "—"}{" "}
                        {ts("participant.participantsLabel", "участников")}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              {ts(
                "participant.noUpcomingEvents",
                "Нет предстоящих мероприятий",
              )}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Ближайшие события */}
        <TabPanel value={activeTab} index={1}>
          {isLoadingRecommendations ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isRecommendationsError ? (
            <Alert severity="error">
              {ts(
                "participant.loadFailed.nearest",
                "Не удалось загрузить ближайшие события",
              )}
            </Alert>
          ) : recommendationsData?.recommendations &&
            recommendationsData.recommendations.length > 0 ? (
            <>
              {/*
               * Подпись нужна, потому что соседняя вкладка называется
               * «Предстоящие» и показывает пересекающийся список. Разница
               * между ними одна и она содержательная: здесь нет того, на что
               * человек уже записан. Без подписи две вкладки выглядели бы
               * одинаковыми (это записано находкой к разбору Сергеем).
               */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 2 }}
              >
                {ts(
                  "participant.nearestCaption",
                  "События, на которые вы ещё не записаны",
                )}
              </Typography>
              <List>
                {recommendationsData.recommendations.map((rec) => (
                  <ListItem key={rec.eventId}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        <StarIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={rec.title}
                      secondary={
                        <>
                          {/*
                           * AD-419 / B4 (22.09.2026): строки «Рекомендовано: …»
                           * и «Рейтинг совпадения: N%» сняты. Сервер их больше
                           * не шлёт, и слать было нечего: `reason` приходил
                           * одной и той же фразой для всех, `score` —
                           * литеральным нулём (который прежний код вдобавок
                           * умножал на сто поверх шкалы 0–100). Вернуть их
                           * можно, когда появится правило подбора.
                           */}
                          {rec.category} • {formatShortDate(rec.startDate)}
                          {rec.location ? (
                            <>
                              {" — "}
                              {rec.location}
                            </>
                          ) : null}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Alert severity="info">
              {ts(
                "participant.noNearestEvents",
                "Нет событий, на которые вы ещё не записаны",
              )}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: История */}
        <TabPanel value={activeTab} index={2}>
          {isLoadingHistory ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isHistoryError ? (
            <Alert severity="error">
              {ts(
                "participant.loadFailed.history",
                "Не удалось загрузить историю участия",
              )}
            </Alert>
          ) : historyData?.history && historyData.history.length > 0 ? (
            <DataTable
              title={ts("participant.historyTitle", "История участия")}
              columns={historyColumns}
              data={historyData.history}
              rowKey={(row) => row.eventId}
              pagination
            />
          ) : (
            <Alert severity="info">
              {ts("participant.noHistory", "История участия пуста")}
            </Alert>
          )}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ParticipantDashboard;
